import { useRecoilState } from "recoil";
import { backendDataAtom } from "recoil/atoms/backendDataAtom.js";
import {
  backendPut,
  backendPost,
  backendGet,
  backendDelete,
} from "services/dataService.js";
import { useSnackbar } from "notistack";
import {
  setRelationState,
  updateRelationState,
  updateRelationStateMany,
} from "./claimRelationStateHelpers.js";

export function useClaimBookingActions() {
  const [backendData, setBackendData] = useRecoilState(backendDataAtom);
  const { enqueueSnackbar } = useSnackbar();

  const relationKey = "bookings";
  const state = backendData?.relations?.[relationKey];

  const setState = (data) =>
    setRelationState(backendData, setBackendData, relationKey, data);
  const updateStateItem = (data) =>
    updateRelationState(backendData, setBackendData, relationKey, data);
  const updateStateItems = (items) =>
    updateRelationStateMany(backendData, setBackendData, relationKey, items);

  const adjustBookingCount = (delta) => {
    setBackendData((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        booking_count: (prev.data?.booking_count ?? 0) + delta,
      },
    }));
  };

  /**
   * After a booking is saved, patch the costs in backendData.data.costs
   * so that remaining_amount / booked_amount stay accurate without a full refetch.
   *
   * claim_costs_new: the cost entries just linked to the saved booking
   * claim_costs_old: the cost entries that were previously linked (edit mode) — so we can reverse them
   */
  const adjustCostBookedAmounts = (
    claim_costs_new = [],
    claim_costs_old = []
  ) => {
    if (!claim_costs_new.length && !claim_costs_old.length) return;

    setBackendData((prev) => {
      const costs = prev.data?.costs;
      if (!costs?.length) return prev;

      // Build delta map: cost_id => net change to booked_amount
      const deltas = {};

      // Old amounts are freed up (negative delta)
      claim_costs_old.forEach(({ id, booking_amount }) => {
        deltas[id] = (deltas[id] ?? 0) - parseFloat(booking_amount || 0);
      });

      // New amounts are consumed (positive delta)
      claim_costs_new.forEach(({ id, booking_amount }) => {
        deltas[id] = (deltas[id] ?? 0) + parseFloat(booking_amount || 0);
      });

      const updated_costs = costs.map((cost) => {
        const delta = deltas[cost.id];
        if (delta === undefined) return cost;
        const new_booked = Math.max(
          0,
          parseFloat(cost.booked_amount || 0) + delta
        );
        const new_remaining = Math.max(
          0,
          parseFloat(cost.amount || 0) - new_booked
        );
        return {
          ...cost,
          booked_amount: new_booked,
          remaining_amount: new_remaining,
        };
      });

      return { ...prev, data: { ...prev.data, costs: updated_costs } };
    });
  };

  /**
   * Buchung erstellen
   *
   * @param data
   * @param options
   * @returns {Promise<void>}
   */
  const createClaimBooking = async (data, options = {}) => {
    await backendPut(`data/bookings/${data.id}`, {
      toSend: {
        ...data,
        amount: data.amount.replace(",", "."),
        iban: (data.iban || "").replace(/\s/g, ""), // Leerzeichen entfernen
      },
      enqueueSnackbar,
      setResult: async (data) => {
        updateStateItem(data);
        adjustBookingCount(1);
      },
      ...options,
    });
  };

  /**
   * Buchung aktualisieren
   *
   * @param data
   * @param options
   * @param options.previous_claim_costs - cost entries previously linked
   * (for edit mode delta calculation)
   * @returns {Promise<void>}
   */
  const updateClaimBooking = async (data, options = {}) => {
    const { previous_claim_costs = [], ...rest_options } = options;
    const is_assignment = data.category === "assignment";
    const is_new = String(data.id).startsWith("new-");
    await backendPut(`data/bookings/${data.id}`, {
      toSend: {
        ...data,
        amount: data.amount.replace(",", "."),
        iban: is_assignment ? "" : (data.iban || "").replace(/\s/g, ""),
        recipient: is_assignment ? "" : data.recipient,
        bic: is_assignment ? "" : data.bic,
        bank_account_id: is_assignment ? null : data.bank_account_id,
      },
      enqueueSnackbar,
      setResult: async (saved) => {
        updateStateItem(saved);
        if (is_new) {
          adjustBookingCount(1);
        }
        adjustCostBookedAmounts(data.claim_costs ?? [], previous_claim_costs);
      },
      ...rest_options,
    });
  };

  /**
   * Buchung löschen
   *
   * @param id
   * @param options
   * @returns {Promise<void>}
   */
  const deleteClaimBooking = async (id, options = {}) => {
    // Fetch the booking's linked costs before deleting so we can reverse their booked amounts
    let claim_costs_to_reverse = [];
    await backendGet(`data/bookings/${id}?include[]=claimCosts`, {
      enqueueSnackbar,
      setResult: (booking) => {
        claim_costs_to_reverse = (booking?.claim_costs ?? []).map((c) => ({
          id: c.id,
          booking_amount: c.booking_amount,
        }));
      },
    });

    await backendDelete(`data/bookings/${id}`, {
      label: "Buchung löschen",
      enqueueSnackbar,
      setResult: async () => {
        // Nach dem Löschen den Eintrag aus dem Zustand entfernen
        if (state?.data) {
          const items = state.data.filter((item) => item.id !== id);
          setState({
            ...state,
            meta: {
              ...state.meta,
              total: (state.meta?.total ?? 1) - 1,
            },
            data: items,
          });
        }

        adjustBookingCount(-1);
        adjustCostBookedAmounts([], claim_costs_to_reverse);
        enqueueSnackbar("Buchung erfolgreich gelöscht", {
          variant: "success",
        });
      },
      ...options,
    });
  };

  /**
   * Buchungsstatus aktualisieren
   *
   * @param id
   * @param status
   * @param options
   * @returns {Promise<void>}
   */
  const updateClaimBookingStatus = async (id, status, options = {}) => {
    await backendPut(`data/bookings/${id}/status`, {
      toSend: {
        status: status,
      },
      enqueueSnackbar,
      setResult: async (res) => {
        updateStateItem(res.data);
        enqueueSnackbar("Buchungsstatus erfolgreich aktualisiert", {
          variant: "success",
        });
      },
      ...options,
    });
  };

  /**
   * Abtretungsbuchung abschließen (erstellt automatisch eine IN-Gegenbuchung)
   */
  const completeAssignmentBooking = async (id, options = {}) => {
    await backendPost(`data/bookings/${id}/complete`, {
      toSend: {},
      enqueueSnackbar,
      setResult: async (res) => {
        const updated = [res.data];
        if (res.counterpart) {
          updated.push(res.counterpart);
          adjustBookingCount(1);
        }
        updateStateItems(updated);
        enqueueSnackbar("Abtretungsbuchung erfolgreich abgeschlossen", {
          variant: "success",
        });
      },
      ...options,
    });
  };

  const findById = (id, options) => {
    const {
      label = "Buchung",
      setIsLoading = () => {},
      setResult = () => {},
    } = options || {};

    return backendGet(`data/bookings/${id}`, {
      label,
      setIsLoading,
      setResult,
      enqueueSnackbar,
    });
  };

  return {
    findById,
    createClaimBooking,
    updateClaimBooking,
    deleteClaimBooking,
    updateClaimBookingStatus,
    completeAssignmentBooking,
    setState,
    state,
  };
}
